import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../context/AuthContext';
import axios from 'axios';
import config from '../../../config';
import PlatefullAILogo from '../../../images/PlatefullAILogoNB.svg';
import StepProgress from '../../../components/StepProgress/StepProgress';
import SignOutLink from '../../../components/SignOutLink/SignOutLink';
import styles from '../Onboard.module.css';

// Import types from AuthContext
type UserFavorite = {
  id: string;
  item_id: string;
  created_at: string;
  updated_at: string;
  item: {
    id: string;
    name: string;
    normalized_name: string;
    type: 'food' | 'drink';
    approved: boolean;
  };
};

interface Item {
  id: string;
  name: string;
  normalized_name: string;
  type: 'food' | 'drink';
  approved: boolean;
}

interface FavoritesStepProps {
  onNext: (data: any) => void;
  onBack: () => void;
  isLoading: boolean;
  error: string;
  user: any;
  onStepClick?: (stepNumber: number) => void;
}

const FavoritesStep: React.FC<FavoritesStepProps> = ({ 
  onNext, 
  onBack, 
  isLoading, 
  error, 
  user, 
  onStepClick 
}) => {
  const { saveUserPreferencesToStorage } = useAuth();
  const [foodSearchQuery, setFoodSearchQuery] = useState('');
  const [drinkSearchQuery, setDrinkSearchQuery] = useState('');
  const [foodSearchResults, setFoodSearchResults] = useState<Item[]>([]);
  const [drinkSearchResults, setDrinkSearchResults] = useState<Item[]>([]);
  const [showFoodSearchResults, setShowFoodSearchResults] = useState(false);
  const [showDrinkSearchResults, setShowDrinkSearchResults] = useState(false);
  const [isSearchingFood, setIsSearchingFood] = useState(false);
  const [isSearchingDrink, setIsSearchingDrink] = useState(false);
  const [userFavorites, setUserFavorites] = useState<UserFavorite[]>([]);

  // Load user favorites on component mount
  useEffect(() => {
    if (user) {
      loadUserFavorites();
    }
  }, [user]);

  const loadUserFavorites = async () => {
    if (!user) return;
    
    try {
      const response = await axios.get(`${config.API_BASE_URL}/api/user-favorites/my-favorites`, {
        headers: {
          'Authorization': `Bearer ${user.accessToken}`,
          'Content-Type': 'application/json'
        }
      });

      setUserFavorites(response.data);
    } catch (error) {
      console.error('Error loading user favorites:', error);
    }
  };

  const searchItems = async (query: string, type: 'food' | 'drink') => {
    if (query.length < 2) {
      if (type === 'food') {
        setFoodSearchResults([]);
        setShowFoodSearchResults(false);
      } else {
        setDrinkSearchResults([]);
        setShowDrinkSearchResults(false);
      }
      return;
    }

    if (type === 'food') {
      setIsSearchingFood(true);
    } else {
      setIsSearchingDrink(true);
    }

    try {
      const response = await axios.get(`${config.API_BASE_URL}/api/user-favorites/search`, {
        params: { q: query, type, limit: 5 },
        headers: {
          'Authorization': `Bearer ${user?.accessToken}`,
          'Content-Type': 'application/json'
        }
      });

      if (type === 'food') {
        setFoodSearchResults(response.data);
        setShowFoodSearchResults(true);
      } else {
        setDrinkSearchResults(response.data);
        setShowDrinkSearchResults(true);
      }
    } catch (error) {
      console.error('Error searching items:', error);
    } finally {
      if (type === 'food') {
        setIsSearchingFood(false);
      } else {
        setIsSearchingDrink(false);
      }
    }
  };

  const handleFoodSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const query = e.target.value;
    setFoodSearchQuery(query);
    searchItems(query, 'food');
  };

  const handleDrinkSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const query = e.target.value;
    setDrinkSearchQuery(query);
    searchItems(query, 'drink');
  };

  const addFavorite = async (item: Item) => {
    if (!user) return;

    // Check if user already has this item as a favorite
    const existingFavorite = userFavorites.find(fav => fav.item_id === item.id);
    if (existingFavorite) {
      return;
    }

    // Check if user has reached the limit for this type (3 foods or 3 drinks)
    const typeFavorites = userFavorites.filter(fav => fav.item.type === item.type);
    if (typeFavorites.length >= 3) {
      return;
    }

    try {
      const response = await axios.post(`${config.API_BASE_URL}/api/user-favorites/my-favorites/by-item`, {
        item_id: item.id
      }, {
        headers: {
          'Authorization': `Bearer ${user.accessToken}`,
          'Content-Type': 'application/json'
        }
      });

      // Add the new favorite to the list
      setUserFavorites(prev => [...prev, response.data]);
      
      // Clear search
      if (item.type === 'food') {
        setFoodSearchQuery('');
        setShowFoodSearchResults(false);
      } else {
        setDrinkSearchQuery('');
        setShowDrinkSearchResults(false);
      }
    } catch (error) {
      console.error('Error adding favorite:', error);
    }
  };

  const removeFavorite = async (favoriteId: string) => {
    if (!user) return;

    try {
      await axios.delete(`${config.API_BASE_URL}/api/user-favorites/my-favorites/by-id/${favoriteId}`, {
        headers: {
          'Authorization': `Bearer ${user.accessToken}`,
          'Content-Type': 'application/json'
        }
      });

      // Remove from local state
      setUserFavorites(prev => prev.filter(fav => fav.id !== favoriteId));
    } catch (error) {
      console.error('Error removing favorite:', error);
    }
  };

  const getFoodFavorites = () => {
    return userFavorites.filter(fav => fav.item.type === 'food');
  };

  const getDrinkFavorites = () => {
    return userFavorites.filter(fav => fav.item.type === 'drink');
  };

  const createCustomItem = async (name: string, type: 'food' | 'drink') => {
    if (!user) return;

    // Check if user has reached the limit for this type (3 foods or 3 drinks)
    const typeFavorites = userFavorites.filter(fav => fav.item.type === type);
    if (typeFavorites.length >= 3) {
      return;
    }

    try {
      const response = await axios.post(`${config.API_BASE_URL}/api/user-favorites/my-favorites`, {
        name: name.trim(),
        type: type
      }, {
        headers: {
          'Authorization': `Bearer ${user.accessToken}`,
          'Content-Type': 'application/json'
        }
      });

      // Add the new favorite to the list
      setUserFavorites(prev => [...prev, response.data]);
      
      // Clear search
      if (type === 'food') {
        setFoodSearchQuery('');
        setShowFoodSearchResults(false);
      } else {
        setDrinkSearchQuery('');
        setShowDrinkSearchResults(false);
      }
    } catch (error) {
      console.error('Error creating custom favorite:', error);
    }
  };

  const handleNext = () => {
    // Save favorites to localStorage for persistence
    saveUserPreferencesToStorage({
      dietary: user?.preferences?.dietary || [],
      healthConcerns: user?.preferences?.healthConcerns || [],
      ingredientPreferences: user?.preferences?.ingredientPreferences || [],
      favorites: userFavorites
    });
    
    onNext({});
  };

  return (
    <div className={styles.stepContainer}>
      <div className={styles.stepHeader}>
        <div className={styles.logoContainer}>
          <img src={PlatefullAILogo} alt="PLATEFULL AI Logo" className={styles.logoImage} />
        </div>
        <h2 className={styles.stepTitle}>
          Favorites
        </h2>
      </div>
      
      <StepProgress 
        currentStep={4} 
        totalSteps={4} 
        stepNames={['Account', 'Dietary', 'Health', 'Favorites']}
        onStepClick={onStepClick}
      />
      
      <div className={styles.stepDescription} style={{marginBottom: '0'}}>
        <p>
          Tell us about your favorite foods and drinks.
          You can add up to 3 of each.
        </p>
      </div>

      {error && <div className={styles.errorMessage}>{error}</div>}

      {/* Favorite Foods Section */}
      <div className={styles.favoritesSection}>
        <h4>Favorite Foods ({getFoodFavorites().length}/3)</h4>
        
        {/* Food Search */}
        <div className={styles.searchSection}>
          <div className={styles.searchContainer}>
            <input
              type="text"
              id="food-search-input"
              name="foodSearch"
              placeholder="Search for foods..."
              value={foodSearchQuery}
              onChange={handleFoodSearchChange}
              className={styles.searchInput}
            />
            {isSearchingFood && <div className={styles.searchSpinner}>Searching...</div>}
          </div>

          {/* Food Search Results */}
          {showFoodSearchResults && foodSearchResults.length > 0 && (
            <div className={styles.searchResults}>
              {foodSearchResults.map((item) => (
                <div key={item.id} className={styles.ingredientCard}>
                  <div className={styles.ingredientHeader}>
                    <span className={styles.ingredientName}>{item.name}</span>
                  </div>
                  <div className={styles.preferenceActions}>
                    <button
                      onClick={() => addFavorite(item)}
                      className={styles.preferenceAction}
                      title="Add to Favorites"
                    >
                      +
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Create Custom Food */}
          {foodSearchQuery.length >= 2 && foodSearchResults.length === 0 && !isSearchingFood && (
            <div className={styles.customItemSection}>
              <p>No foods found. Create custom:</p>
              <button
                onClick={() => createCustomItem(foodSearchQuery, 'food')}
                className={styles.customItemButton}
              >
                Add "{foodSearchQuery}"
              </button>
            </div>
          )}
        </div>

        {/* Current Food Favorites */}
        {getFoodFavorites().length > 0 && (
          <div className={styles.preferenceCategory}>
            <div className={styles.preferenceTags}>
              {getFoodFavorites().map((favorite) => (
                <div key={favorite.id} className={`${styles.preferenceTag} ${styles.love}`}>
                  <span className={styles.tagType}>Food</span>
                  <span className={styles.tagLabel}>{favorite.item.name}</span>
                  <button
                    onClick={() => removeFavorite(favorite.id)}
                    className={styles.removeTag}
                    title="Remove"
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Favorite Drinks Section */}
      <div className={styles.favoritesSection}>
        <h4>Favorite Drinks ({getDrinkFavorites().length}/3)</h4>
        
        {/* Drink Search */}
        <div className={styles.searchSection}>
          <div className={styles.searchContainer}>
            <input
              type="text"
              id="drink-search-input"
              name="drinkSearch"
              placeholder="Search for drinks..."
              value={drinkSearchQuery}
              onChange={handleDrinkSearchChange}
              className={styles.searchInput}
            />
            {isSearchingDrink && <div className={styles.searchSpinner}>Searching...</div>}
          </div>

          {/* Drink Search Results */}
          {showDrinkSearchResults && drinkSearchResults.length > 0 && (
            <div className={styles.searchResults}>
              {drinkSearchResults.map((item) => (
                <div key={item.id} className={styles.ingredientCard}>
                  <div className={styles.ingredientHeader}>
                    <span className={styles.ingredientName}>{item.name}</span>
                  </div>
                  <div className={styles.preferenceActions}>
                    <button
                      onClick={() => addFavorite(item)}
                      className={styles.preferenceAction}
                      title="Add to Favorites"
                    >
                      +
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Create Custom Drink */}
          {drinkSearchQuery.length >= 2 && drinkSearchResults.length === 0 && !isSearchingDrink && (
            <div className={styles.customItemSection}>
              <p>No drinks found. Create custom:</p>
              <button
                onClick={() => createCustomItem(drinkSearchQuery, 'drink')}
                className={styles.customItemButton}
              >
                Add "{drinkSearchQuery}"
              </button>
            </div>
          )}
        </div>

        {/* Current Drink Favorites */}
        {getDrinkFavorites().length > 0 && (
          <div className={styles.preferenceCategory}>
            <div className={styles.preferenceTags}>
              {getDrinkFavorites().map((favorite) => (
                <div key={favorite.id} className={`${styles.preferenceTag} ${styles.love}`}>
                  <span className={styles.tagType}>Drink</span>
                  <span className={styles.tagLabel}>{favorite.item.name}</span>
                  <button
                    onClick={() => removeFavorite(favorite.id)}
                    className={styles.removeTag}
                    title="Remove"
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <div className={styles.buttonContainer}>
        <button
          type="button"
          onClick={onBack}
          className={styles.secondaryButton}
          disabled={isLoading}
        >
          Back
        </button>
        <button
          type="button"
          onClick={handleNext}
          disabled={isLoading}
          className={styles.primaryButton}
        >
          <span>
            {isLoading && <span className={styles.loadingSpinner}></span>}
            {isLoading ? 'Saving...' : 'Continue'}
          </span>
        </button>
      </div>
      
      <SignOutLink />
    </div>
  );
};

export default FavoritesStep;
